import { Row, Col, Card, Table, Button, Input, Tag, Tooltip, Image, Switch, Tabs, Form, Space } from "antd";
import { useState, useEffect, useRef } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import useDebounce from "../../hooks/useDebounce";
import ShowTotal from "../../components/ShowTotal";
import ConfirmationBox from "../../components/ConfirmationBox";
import apiPath from "../../constants/apiPath";
import moment from 'moment';
import notfound from "../../assets/images/no-img.png";
import { CloseOutlined, CheckOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from "react-router-dom";
import { getSortOrder } from "../../constants/conts";
import { CrossIcon, EyeIcon, FilterX, PencilLineIcon, RefreshCcw, Upload, ViewIcon } from "lucide-react";
import BrandSelect from "../../components/select/BrandSelect";
import CategorySelect from "../../components/select/CategorySelect";
import SegmentSelect from "../../components/select/SegmentSelect";
import VehicleManufacturerSelect from "../../components/select/VehicleManufacturerSelect";
import VehicleSelect from "../../components/select/VehicleSelect";
import BulkUpdateLanguage from "./BulkUpdateLanguage";
const Search = Input.Search;
function Index() {

  const sectionName = "Product";
  const routeName = "product";

  const api = {
    status: apiPath.statusProduct,
    addEdit: apiPath.addEditProduct,
    list: apiPath.listProduct
  }

  const location = useLocation();
  const navigate = useNavigate();

  const query = new URLSearchParams(location.search);
  const initialPage = parseInt(query.get("page")) || 1;
  const initialPageSize = parseInt(query.get("pageSize")) || 20;
  const initialSearch = query.get("search") || "";
  const initialSort = query.get("sort") || "";
  const initialTab = query.get("tab") || "all";

  const [pagination, setPagination] = useState({
    current: initialPage,
    pageSize: initialPageSize,
  });

  const [searchText, setSearchText] = useState(initialSearch);
  const [sortBy, setSortBy] = useState(initialSort);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [brandId, setBrandId] = useState(null);
  const [categoryId, setCategoryId] = useState(null);
  const [segmentId, setSegmentId] = useState(null);
  const [vehicleManufacturerId, setVehicleManufacturerId] = useState(null);
  const [vehicleId, setVehicleId] = useState(null);

  const { request } = useRequest()
  const [list, setList] = useState([])
  const { showConfirm } = ConfirmationBox()
  const [loading, setLoading] = useState(false)
  const [refresh, setRefresh] = useState(false)
  const lastFiltersRef = useRef({});
  const debouncedSearchText = useDebounce(searchText, 500);

  const columns = [
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      render: (_, { image_url }) => <Image width={60} height={60} src={image_url || notfound} />
    },
    {
      title: "Part Number",
      dataIndex: "part_number",
      key: "part_number",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "part_number"),
      render: (_, { part_number }) => part_number ? <span className="cap">{part_number}</span> : '-',
    },
    {
      title: "Vehicle Mapping",
      dataIndex: "vehicle_mapping_option",
      key: "vehicle_mapping_option",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "vehicle_mapping_option"),
      render: (_, { vehicle_mapping_option }) => {
        if (!vehicle_mapping_option) return '-';

        const colorMap = {
          'all': 'green',
          'manufacturers': 'red',
          'specific': 'purple'
        };

        return (
          <Tag color={colorMap[vehicle_mapping_option] || 'default'} className="cap">
            {vehicle_mapping_option}
          </Tag>
        );
      }
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "price"),
      render: (_, { price }) => price ? `₹${price}` : '-',
    },
    {
      title: "Segment",
      dataIndex: "segment",
      key: "segment",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "segment"),
      render: (_, { segment }) =>
        segment?.en_name ? <span className="cap">{segment?.en_name}</span> : '-',
    },
    {
      title: "Brand",
      dataIndex: "brand",
      key: "brand",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "brand"),
      render: (_, { brand }) =>
        brand?.en_name ? <span className="cap">{brand?.en_name}</span> : '-',
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "category"),
      render: (_, { category }) =>
        category?.en_name ? <span className="cap">{category?.en_name}</span> : '-',
    },
    {
      title: "Status",
      key: "is_active",
      filters: activeTab === 'all' ? [
        {
          text: 'Active',
          value: true,
        },
        {
          text: 'Inactive',
          value: false,
        },
      ] : null,
      render: (_, { is_active, _id }) => {
        const switchElement = (
          <Switch
            checked={is_active}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            loading={loading}
            disabled={activeTab !== 'all'}
            onChange={(checked) =>
              showConfirm({
                record: _id,
                path: api.status,
                // onLoading: () => setLoading(true),
                onSuccess: () => setRefresh((prev) => !prev),
                is_active: checked,
              })
            }
            className={is_active ? "custom-switch-checked" : "custom-switch-unchecked"}
          />
        );

        return activeTab !== 'all' ? (
          <Tooltip title="Status can only be changed after vehicle attachment">
            <span style={{ cursor: 'not-allowed' }}>{switchElement}</span>
          </Tooltip>
        ) : (
          switchElement
        );
      },
    },
    {
      title: "Created On",
      key: "created_at",
      dataIndex: "created_at",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "created_at"),
      render: (_, { created_at }) => {
        return (
          moment(created_at).format('DD-MMM-YYYY')
        );
      },
    },
    {
      title: "Action",
      fixed: 'right',
      render: (_, record) => {
        return (
          <>

            <Tooltip title={"Update " + sectionName} color={"purple"} key={"update"}>
              <Button onClick={() => { edit(record._id) }}>
                <PencilLineIcon size={15} />
              </Button>
            </Tooltip>

            <Tooltip title={"View " + sectionName} color={"purple"} key={"view"}>
              <Button onClick={() => { view(record._id) }}>
                <EyeIcon size={15} />
              </Button>
            </Tooltip>
          </>
        );
      },
    },
  ];

  const view = (id) => {
    navigate(`/${routeName}/view/${id}${location.search}`);
  };

  const edit = (id) => {
    navigate(`/${routeName}/update/${id}${location.search}`);
  };

  useEffect(() => {
    const params = new URLSearchParams();

    if (activeTab && activeTab !== "all") params.set("tab", activeTab);
    if (pagination.current > 1) params.set("page", pagination.current);
    if (pagination.pageSize !== 20) params.set("pageSize", pagination.pageSize);
    if (debouncedSearchText) params.set("search", debouncedSearchText);
    if (sortBy) params.set("sort", sortBy);

    navigate({ search: params.toString() }, { replace: true });
  }, [activeTab, pagination.current, pagination.pageSize, debouncedSearchText, sortBy, navigate]);

  const fetchData = (pagination, filters, sortValue = '') => {
    const filterActive = filters ? filters.is_active : null

    let tabParam = '&without_vehicle_attach=false';

    if (activeTab === 'without-vehicle') {
      tabParam = '&without_vehicle_attach=true';
    }

    request({
      url:
        api.list +
        `?status=${filterActive ? filterActive.join(',') : ''}` +
        `&page=${pagination.current}` +
        `&limit=${pagination.pageSize}` +
        `&search=${debouncedSearchText}` +
        `&sort=${sortValue || ''}` +
        tabParam +
        (brandId ? `&brand_id=${brandId}` : '') +
        (categoryId ? `&category_id=${categoryId}` : '') +
        (segmentId ? `&segment_id=${segmentId}` : '') +
        (vehicleManufacturerId ? `&vehicle_manufacturer_id=${vehicleManufacturerId}` : '') +
        (vehicleId ? `&vehicle_id=${vehicleId}` : ''),
      method: 'GET',
      onSuccess: (data) => {
        setLoading(false)
        setList(data.data.list.docs)
        setPagination(prev => ({
          ...prev,
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: data.data.list.totalDocs
        }));
      },
      onError: (error) => {
        setLoading(false);
        const errorMsg = error?.response?.data?.message || error?.message || "Failed to fetch products";
        ShowToast(errorMsg, Severty.ERROR);
      }
    })
  }

  const handleChange = (pag, filters, sorter) => {
    const { order, field } = sorter || {};

    let nextSort = '';
    if (order) {
      nextSort = `${field}:${order === 'ascend' ? 'asc' : 'desc'}`;
    }

    lastFiltersRef.current = filters || {};

    setSortBy(nextSort);

    setPagination(prev => ({
      ...prev,
      current: pag?.current || 1,
      pageSize: pag?.pageSize || prev.pageSize,
    }));
  };

  const onSearch = (e) => {
    setSearchText(e.target.value)
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const hanldeRefresh = () => {
    setSearchText('');
    setBrandId(null);
    setCategoryId(null);
    setSegmentId(null);
    setVehicleManufacturerId(null);
    setVehicleId(null);
    setPagination({ current: 1, pageSize: 20 });
    setRefresh(prev => !prev);
    setSortBy("");
    navigate({ search: "" }, { replace: true });
  };

  const clearFilters = () => {
    setSearchText('');
    setBrandId(null);
    setCategoryId(null);
    setSegmentId(null);
    setVehicleManufacturerId(null);
    setVehicleId(null);
    setPagination(prev => ({ ...prev, current: 1 }));
    setRefresh(prev => !prev);
  };

  useEffect(() => {
    setLoading(true)
    fetchData(pagination, lastFiltersRef.current, sortBy)
  }, [refresh, debouncedSearchText, sortBy, pagination.current, pagination.pageSize, activeTab, brandId, categoryId, segmentId, vehicleManufacturerId, vehicleId])

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleAddNew = () => {
    navigate(`/${routeName}/add${location.search}`);
  };

  const handleImportClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/${routeName}/import${location.search}`);
  };

  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              variant={false}
              className="criclebox tablespace mb-24"
              title={sectionName + " Management"}
              extra={
                <>
                  <Search
                    size="large"
                    onChange={onSearch}
                    value={searchText}
                    onPressEnter={onSearch}
                    placeholder="Search By Part Number, English Name..."
                    style={{ minWidth: 280 }}
                  />

                  <div className="flex justify-end w-100 gap-2">
                    {(brandId || categoryId || segmentId || vehicleManufacturerId || vehicleId || searchText) && (
                      <Button
                        type="text"
                        onClick={clearFilters}
                        danger
                      >
                        <FilterX size={15} />
                        Clear Filters
                      </Button>
                    )}

                    <Button
                      onClick={hanldeRefresh}
                      type="primary"
                    >
                      <RefreshCcw size={15} />
                      Refresh
                    </Button>

                    {/* <Button
                      onClick={handleAddNew}
                      type="primary"
                    >
                      Add New {sectionName}
                    </Button> */}

                    <Button
                      htmlType="button"
                      onClick={handleImportClick}
                      type="primary"
                    >
                      <Upload className="w-[18px]" />
                      Import Bulk Products
                    </Button>
                  </div>
                </>
              }
            >

              <Row gutter={[24, 0]} align="middle border-bottom pb-2">
                <Col xs={24} sm={12} md={8}>
                  <BrandSelect
                    value={brandId}
                    onChange={(val) => {
                      setBrandId(val);
                      setCategoryId(null);
                      setSegmentId(null);
                    }}
                  />
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <CategorySelect
                    brandId={brandId}
                    disabled={!brandId}
                    shouldFetch={!!brandId}
                    value={categoryId}
                    onChange={(val) => {
                      setCategoryId(val);
                      setSegmentId(null);
                    }}
                  />
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <SegmentSelect
                    brandId={brandId}
                    disabled={!brandId}
                    shouldFetch={!!brandId}
                    value={segmentId}
                    onChange={setSegmentId}
                  />
                </Col>
              </Row>

              <Row gutter={[24, 0]} align="middle border-bottom pb-2">
                <Col xs={24} sm={12} md={8}>
                  <VehicleManufacturerSelect
                    value={vehicleManufacturerId}
                    onChange={(val) => {
                      setVehicleManufacturerId(val);
                      setVehicleId(null);
                    }}
                  />
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <VehicleSelect
                    manufacturerId={vehicleManufacturerId}
                    value={vehicleId}
                    onChange={setVehicleId}
                  />
                </Col>
              </Row>

              <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                items={[
                  {
                    key: 'all',
                    label: 'All Products',
                    children: (
                      <>
                        <h4 className="text-right mb-1">{pagination.total ? ShowTotal(pagination.total) : ShowTotal(0)}</h4>
                        <div className="table-responsive customPagination">
                          <Table
                            rowKey={(record) => record._id}
                            loading={loading}
                            columns={columns}
                            dataSource={list}
                            pagination={{
                              current: pagination.current,
                              pageSize: pagination.pageSize,
                              total: pagination.total,
                              showSizeChanger: true,
                              showQuickJumper: true,
                              responsive: true,
                              defaultPageSize: 20,
                              pageSizeOptions: ['10', '20', '30', '50', '100']
                            }}
                            onChange={handleChange}
                            className="ant-border-space"
                          />
                        </div>
                      </>
                    )
                  },
                  {
                    key: 'without-vehicle',
                    label: 'Products without Vehicle Attach',
                    children: (
                      <>
                        <h4 className="text-right mb-1">{pagination.total ? ShowTotal(pagination.total) : ShowTotal(0)}</h4>
                        <div className="table-responsive customPagination">
                          <Table
                            rowKey={(record) => record._id}
                            loading={loading}
                            columns={columns}
                            dataSource={list}
                            pagination={{
                              current: pagination.current,
                              pageSize: pagination.pageSize,
                              total: pagination.total,
                              showSizeChanger: true,
                              showQuickJumper: true,
                              responsive: true,
                              defaultPageSize: 20,
                              pageSizeOptions: ['10', '20', '30', '50', '100']
                            }}
                            onChange={handleChange}
                            className="ant-border-space"
                          />
                        </div>
                      </>
                    )
                  },
                  {
                    key: 'bulk-update',
                    label: 'Bulk Update Languages',
                    children: (
                      <BulkUpdateLanguage />
                    )
                  }
                ]}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default Index;
